import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import { isBlockLevelContent, isMetadataContent } from './dom/content';
import { dirtyHtmlToDom } from './dom/htmlToDom';
import { isElement, isElementWithTagName, isHTMLOrSVGElement, isNode } from './dom/node';
import { isBreakElement } from './dom/paragraph';
import { createIterable } from './iterable';
import { expandWhitespace } from './textToHtml';
import {
  collapseWhitespace,
  isOnlyCollapsibleWhitespace,
  normalizeContentEditableWhitespace,
} from './whitespace';

/** This covers both strings and TrustedHTML. */
interface StringLike {
  toString(): string;
}

export function htmlToText(nodeOrHtml: Node | StringLike): string {
  if (isNode(nodeOrHtml)) {
    return nodeToText(nodeOrHtml);
  }

  return dirtyHtmlToDom(nodeOrHtml.toString(), nodeToText);
}

export function nodeToText(node: Node, shouldPreserveWhitespace = false): string {
  // String concatenation is faster than Array.join().
  // https://jsperf.com/yet-another-array-vs-concat
  let text = '';
  let isFirstLine = true;

  for (const line of nodeToLines(node, shouldPreserveWhitespace)) {
    if (isFirstLine) {
      isFirstLine = false;
    } else {
      text += '\n';
    }

    text += line;
  }

  return normalizeContentEditableWhitespace(text);
}

const LINE_SEPARATOR_REGEXP = /(?:\r\n|\r|\n)/;

// TODO: Benchmark generators.
export function nodeToLines(node: Node, shouldPreserveWhitespace = false): Iterable<string> {
  function* flushLine(line: string) {
    yield shouldPreserveWhitespace ? line : collapseWhitespace(line);
  }

  function* flushLineUnlessEmpty(line: string | undefined) {
    if (isUndefined(line)) {
      return;
    }

    if (!shouldPreserveWhitespace && isOnlyCollapsibleWhitespace(line)) {
      return;
    }

    yield* flushLine(line);
  }

  return createIterable(function* createIterator() {
    let wasPreviousLineVerticalMargin = false;
    let currentLine: string | undefined;

    for (const token of nodeToLineTokens(node, shouldPreserveWhitespace)) {
      if (isString(token)) {
        // Collect string tokens to build a line of text.
        currentLine = (currentLine || '') + token;
      } else if (token === LINE_BREAK) {
        // Flush either the current line or an empty line.
        yield* flushLine(currentLine ?? '');

        wasPreviousLineVerticalMargin = false;
        currentLine = undefined;
      } else if (token === BLOCK_BOUNDARY) {
        // Flush the current line, if it isn't empty.
        yield* flushLineUnlessEmpty(currentLine);

        wasPreviousLineVerticalMargin = false;
        currentLine = undefined;
      } else if (token === VERTICAL_MARGIN) {
        // Same as a line break, but collapse consecutive margins.
        if (!wasPreviousLineVerticalMargin) {
          yield* flushLine(currentLine ?? '');

          wasPreviousLineVerticalMargin = true;
          currentLine = undefined;
        }
      }
    }

    // Flush the current line, if it isn't empty.
    yield* flushLineUnlessEmpty(currentLine);
  });
}

// Use symbols for non-string tokens, so they can never conflict.
type LineToken = string | typeof LINE_BREAK | typeof BLOCK_BOUNDARY | typeof VERTICAL_MARGIN;

const LINE_BREAK = Symbol('LINE_BREAK');
const BLOCK_BOUNDARY = Symbol('BLOCK_BOUNDARY');
const VERTICAL_MARGIN = Symbol('VERTICAL_MARGIN');

function nodeToLineTokens(node: Node, shouldPreserveWhitespace: boolean): Iterable<LineToken> {
  return createIterable(function* createIterator() {
    for (const childNode of node.childNodes) {
      if (isMetadataContent(childNode)) {
        continue;
      }

      if (isBlockLevelContent(childNode)) {
        if (hasMarginTop(childNode)) {
          yield VERTICAL_MARGIN;
        }

        yield BLOCK_BOUNDARY;
        yield* nodeToLineTokens(childNode, isElementWithTagName(childNode, 'pre'));
        yield BLOCK_BOUNDARY;

        if (hasMarginBottom(childNode)) {
          yield VERTICAL_MARGIN;
        }

        continue;
      }

      if (isBreakElement(childNode)) {
        yield LINE_BREAK;
        continue;
      }

      if (isElement(childNode)) {
        if (childNode.hasAttribute('alt')) {
          yield childNode.getAttribute('alt') || '';
        }

        yield* nodeToLineTokens(childNode, shouldPreserveWhitespace);
        continue;
      }

      const { textContent } = childNode;
      if (!textContent) {
        continue;
      }

      if (shouldPreserveWhitespace) {
        for (const line of textContent.split(LINE_SEPARATOR_REGEXP)) {
          yield expandWhitespace(line);
          yield LINE_BREAK;
        }
      } else {
        yield textContent;
      }
    }
  });
}

/** Tags that have vertical margins by default in most browsers. */
const CONTENT_WITH_VERTICAL_MARGINS_TAG_NAMES = new Set([
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'P',
  'UL',
  'OL',
  'DL',
  'BLOCKQUOTE',
  'FIGURE',
  'PRE',
]);

function hasMarginTop(node: Node): boolean {
  return hasMargin(node, 'marginTop');
}

function hasMarginBottom(node: Node): boolean {
  return hasMargin(node, 'marginBottom');
}

function hasMargin(node: Node, styleProp: 'marginTop' | 'marginBottom'): boolean {
  const margin = isHTMLOrSVGElement(node) ? node.style[styleProp] : null;
  if (margin) {
    return Boolean(margin.match(/[1-9]/));
  }

  return isElement(node) && CONTENT_WITH_VERTICAL_MARGINS_TAG_NAMES.has(node.tagName);
}
